import { ROW_HEIGHT } from "@/components/CaseListing/utils";
import { ScanThumbnail } from "@/components/Thumbnail/ScanThumbnail";
import { selectScans, selectSelectedScans } from "@/store/slices/casesSlice";
import { RootState } from "@/store/store";
import { Paper, SxProps, Theme, Typography } from "@mui/material";
import { createSelector } from "@reduxjs/toolkit";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const selectSelectedScanInformation = createSelector(
  [(state: RootState) => selectScans(state), (state: RootState) => selectSelectedScans(state)],
  (scans, selectedScans) => {
    const firstSelectedUpload = Object.entries(selectedScans).find(([_, scanIds]) => scanIds.length > 0);
    if (firstSelectedUpload === undefined) {
      return {};
    }

    const numSelected = Object.values(selectedScans)
      .filter((scanIds) => scanIds != null)
      .flat().length;
    const [upload, [scanId]] = firstSelectedUpload;
    const uploadId = Number(upload);
    const scan = scans[scanId];

    return { uploadId, scanId, scan, numSelected };
  }
);

export const ScanDropIndicator = () => {
  const { t } = useTranslation("cases");
  const { uploadId, scanId, scan, numSelected } = useSelector(selectSelectedScanInformation);

  if (uploadId === undefined || scanId === undefined || scan === undefined) {
    return <></>;
  }

  const containerSx: SxProps<Theme> = {
    width: "400px",
    height: `${ROW_HEIGHT}px`,
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    borderRadius: "8px",
    pointerEvents: "none",
    paddingX: "8px",
    backgroundColor: (theme: Theme) => theme.surface.lowest,
    overflow: "hidden"
  };

  return (
    <>
      <Paper
        elevation={1}
        sx={{
          ...containerSx
        }}
      >
        <ScanThumbnail scanId={scanId} caseId={uploadId} />
        <Typography
          noWrap={numSelected === 1}
          sx={{
            containerType: "inline-size",
            width: "100%",
            ...(numSelected > 1
              ? {
                  display: "flex",
                  alignItems: "center",
                  gap: "2px"
                }
              : {})
          }}
        >
          <Trans
            i18nKey={"message.draggedCase"}
            components={{
              shrink: <Typography sx={{ flexShrink: 1 }} component="span" noWrap />,
              span: <Typography sx={{ flexShrink: 0 }} component="span" noWrap />
            }}
            t={t}
            values={{ name: scan.filename, count: numSelected - 1 }}
          />
        </Typography>
      </Paper>
      {numSelected > 1 && <Paper sx={{ ...containerSx, position: "absolute", top: "4px", left: "4px", zIndex: "-1" }} />}
    </>
  );
};
